import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { GrCheckmark } from "react-icons/gr";
import { media } from "@glitz/core";
import { styled } from "@glitz/react";
import Base, { ContentWidth } from "../components/basic/Base";
import Loader from "../components/basic/Loader";
import Button from "../components/basic/Button";
import Checkbox from "../components/basic/Checkbox";
import NumberField from "../components/basic/NumberField";
import SectionHeading from "../components/basic/SectionHeading";
import SegmentedControl, {
  type SegmentedOption,
} from "../components/basic/SegmentedControl";
import WizardSteps from "../components/basic/WizardSteps";
import IntakePlanTimeline from "../components/IntakePlanTimeline";
import CyclingBottlePlanner from "../components/CyclingBottlePlanner";
import { useCart } from "../context/CartContext";
import { useViewport } from "../hooks/useViewport";
import { Theme, Size } from "../theme";
import {
  spacing,
  fontSize,
  fontWeight,
  green,
  darkGreen,
  red,
  darkRed,
  orange,
  grey,
  darkGrey,
  lightGrey,
  mediumGrey,
  black,
  white,
} from "../theme";
import {
  type CalcProduct,
  type EnergyCatalog,
  type EnergySport,
  type CaffeineFilterMode,
  ENERGY_CALC_SPORTS,
  ENERGY_SPORT_LABELS,
  CAFFEINE_FILTER_MODES,
  CAFFEINE_FILTER_LABELS,
  DEFAULT_PRERACE_MG_PER_KG,
  PRERACE_MG_PER_KG_MIN,
  PRERACE_MG_PER_KG_MAX,
  buildBottlePackageLine,
  buildIntakeTimeline,
  buildPackageLines,
  buildPreracePackageLines,
  buildTriathlonSegmentPackageLines,
  preraceCaffeineDosingHintSv,
  sumPreraceCaffeineMgBeforeIndex,
  catalogHasAnyUsableProduct,
  filterCatalogByCaffeine,
  recommendedBottleCount,
  isCaffeineFilterMode,
  isEnergySport,
  suggestedPreloadCarbs,
  targetCarbsDuringRace,
  totalHoursFromParts,
  totalHoursFromHms,
  totalTriathlonHours,
  unitLabelSv,
  BULK_PURCHASE_UNIT_LABEL_SV,
  BULK_PURCHASE_UNIT_LABEL_PLURAL_SV,
  effectiveCarbsPerUnit,
  normalizeEnergySlotConfig,
  preraceCaffeineMgRange,
  packageLineFlavorGroupKey,
  flavorAlternativesForLine,
  PRERACE_PRODUCT_NITRATE_ID,
  resolvePackageLineWithFlavor,
  expandPackageLinesToSingleUnits,
  rebalanceGelMatExpandedPackageLines,
  splitNonBottlePackageLinesToSingleUnitRows,
  packageLinesIncludeSportDryck,
  carbAllocationMismatchForLine,
} from "../lib/energyCalculator";
import {
  CYCLING_DISTANCE_PRESETS,
  DISTANCE_PRESET_CUSTOM,
  RUNNING_DISTANCE_PRESETS,
  SKIING_DISTANCE_PRESETS,
  TRIATHLON_DISTANCE_PRESETS,
  defaultDistancePresetId,
  formatPresetNumberForField,
  getDefaultDurationForSimplePreset,
  getSimpleDistancePresetKm,
  getTriathlonPresetById,
  getTriathlonPresetTimeDefaults,
  resolveRaceDistanceKm,
} from "../lib/energyCalculatorDistances";
import {
  buildEnergyCalcSearchParams,
  energyCalcSearchParamsToStableString,
  parseEnergyCalcUrlParams,
  readSportFromSearchParams,
  type EnergyCalcUrlSnapshot,
} from "../lib/energyCalculatorUrlState";

const API_BASE = process.env.REACT_APP_API_URL || "";

function formatPriceKr(n: number): string {
  return `${Math.round(n)} kr`;
}

function unitPriceFromProduct(priceStr: string): number {
  const n = parseFloat(priceStr);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function distancePresetOptions(
  sport: EnergySport
): { id: string; label: string }[] {
  const custom = { id: DISTANCE_PRESET_CUSTOM, label: "Ange egen" };
  switch (sport) {
    case "triathlon":
      return [
        ...TRIATHLON_DISTANCE_PRESETS.map((p) => ({ id: p.id, label: p.label })),
        custom,
      ];
    case "cycling":
      return [
        ...CYCLING_DISTANCE_PRESETS.map((p) => ({ id: p.id, label: p.label })),
        custom,
      ];
    case "running":
      return [
        ...RUNNING_DISTANCE_PRESETS.map((p) => ({ id: p.id, label: p.label })),
        custom,
      ];
    case "skiing":
      return [
        ...SKIING_DISTANCE_PRESETS.map((p) => ({ id: p.id, label: p.label })),
        custom,
      ];
  }
}

function hmsToTotalSeconds(h: number, m: number, sec: number): number {
  return (
    Math.max(0, h) * 3600 +
    Math.max(0, Math.min(59, m)) * 60 +
    Math.max(0, Math.min(59, sec))
  );
}

function totalSecondsToHms(total: number): { h: number; m: number; s: number } {
  const t = Math.max(0, Math.floor(total));
  return {
    h: Math.floor(t / 3600),
    m: Math.floor((t % 3600) / 60),
    s: t % 60,
  };
}

const RunStepperRow = styled.div({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: spacing.m,
  justifyContent: "flex-start",
  alignItems: "flex-end",
});

const RunSubsectionTitle = styled.h4({
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  color: black,
  margin: { top: 0, right: 0, bottom: spacing.xs, left: 0 },
});

const RunSubsectionIntro = styled.p({
  fontSize: fontSize.xxs,
  color: darkGrey,
  lineHeight: "1.55",
  margin: { top: 0, right: 0, bottom: spacing.m, left: 0 },
});

const RunSubsectionDivider = styled.div({
  height: "1px",
  backgroundColor: grey,
  margin: { top: spacing.s, bottom: spacing.m },
  width: "100%",
});

/** Förval vid val av kön (kan justeras med stegkontroller). */
const PERSON_DEFAULTS = {
  male: { ageYears: 30, heightCm: 180, weightKg: 80 },
  female: { ageYears: 30, heightCm: 168, weightKg: 65 },
} as const;

/** Step-för-steg-flödet i energikalkylatorn (motsvarar CheckoutSteps i kassan). */
const WIZARD_STEPS = [
  { number: 1, label: "Förutsättningar" },
  { number: 2, label: "Energipaket" },
  { number: 3, label: "Energiplan" },
] as const;
const WIZARD_MIN_STEP = 1;
const WIZARD_MAX_STEP = WIZARD_STEPS.length;

const EnergyCalculatorPage: React.FC = () => {
  const { addToCart } = useCart();
  const { isMobile } = useViewport();
  const [searchParams, setSearchParams] = useSearchParams();
  const [catalog, setCatalog] = useState<EnergyCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [distancePresetId, setDistancePresetId] = useState<string>(() =>
    defaultDistancePresetId("triathlon")
  );
  /** Eget val: km (cykel, löp, skidor) */
  const [customDistanceKm, setCustomDistanceKm] = useState<string>("");
  /** Eget val triathlon */
  const [customTriSwimM, setCustomTriSwimM] = useState<string>("");
  const [customTriBikeKm, setCustomTriBikeKm] = useState<string>("");
  const [customTriRunKm, setCustomTriRunKm] = useState<string>("");

  const [hours, setHours] = useState<number>(3);
  const [minutes, setMinutes] = useState<number>(0);
  /** Löpning, cykel, skidor (triathlon en-tidsläge använder bara tim+min) */
  const [timeSeconds, setTimeSeconds] = useState<number>(0);
  const [carbsPerHour, setCarbsPerHour] = useState<number>(60);

  const [paceMinInput, setPaceMinInput] = useState<string>("5");
  const [paceSecInput, setPaceSecInput] = useState<string>("0");
  const [cyclingKmhInput, setCyclingKmhInput] = useState<string>("30");
  const [useCyclingOwnStops, setUseCyclingOwnStops] = useState<boolean>(false);
  const [cyclingStopsKm, setCyclingStopsKm] = useState<number[]>([]);

  const skipPaceSyncRef = useRef(false);
  const skipKmhSyncRef = useRef(false);
  const urlSyncingRef = useRef(false);
  const lastSyncedUrlStableRef = useRef<string | null>(null);
  const skipPresetTimeFromUrlRef = useRef(false);
  const [added, setAdded] = useState(false);

  /** Triathlon: separata tider per gren */
  const [triathlonSplitMode, setTriathlonSplitMode] = useState(true);
  const [triSwimH, setTriSwimH] = useState(0);
  const [triSwimM, setTriSwimM] = useState(30);
  const [triBikeH, setTriBikeH] = useState(2);
  const [triBikeM, setTriBikeM] = useState(30);
  const [triRunH, setTriRunH] = useState(1);
  const [triRunM, setTriRunM] = useState(0);

  /** Block 1 — person (kön/ålder/längd används ej i beräkning ännu; vikt → koffein före start) */
  const [personSex, setPersonSex] = useState<"male" | "female">("male");
  const [personAgeYears, setPersonAgeYears] = useState<number>(
    PERSON_DEFAULTS.male.ageYears
  );
  const [personHeightCm, setPersonHeightCm] = useState<number>(
    PERSON_DEFAULTS.male.heightCm
  );
  const [personWeightKg, setPersonWeightKg] = useState<number>(
    PERSON_DEFAULTS.male.weightKg
  );

  const [preraceMgPerKg, setPreraceMgPerKg] = useState(DEFAULT_PRERACE_MG_PER_KG);

  /** Radnyckel → vald Woo-produkt (smak/variant inom samma flavorGroups-grupp). */
  const [flavorChoicesByLineKey, setFlavorChoicesByLineKey] = useState<
    Record<string, number>
  >({});

  /** Radnyckel → true om raden är bortvald (ska inte räknas i total eller varukorg). */
  const [excludedPackageLineKeys, setExcludedPackageLineKeys] = useState<
    Record<string, boolean>
  >({});

  const applyPersonProfileForSex = (sex: "male" | "female") => {
    const d = PERSON_DEFAULTS[sex];
    setPersonAgeYears(d.ageYears);
    setPersonHeightCm(d.heightCm);
    setPersonWeightKg(d.weightKg);
  };

  const sport: EnergySport = useMemo(() => {
    const p = searchParams.get("sport");
    return isEnergySport(p) ? p : "triathlon";
  }, [searchParams]);

  const setSport = useCallback(
    (next: EnergySport) => {
      const defDist = defaultDistancePresetId(next);
      setDistancePresetId(defDist);
      urlSyncingRef.current = true;
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("sport", next);
      nextParams.set("dist", defDist);
      for (const k of [
        "th", "tm", "ts", "tsec", "tsplit",
        "tsh", "tsm", "tbh", "tbm", "trh", "trm",
        "pm", "ps", "kmh", "dkm", "tswim", "tbike", "trun",
      ]) {
        nextParams.delete(k);
      }
      setSearchParams(nextParams, { replace: true });
      requestAnimationFrame(() => {
        urlSyncingRef.current = false;
      });
    },
    [searchParams, setSearchParams]
  );

  const caffeineMode: CaffeineFilterMode = useMemo(() => {
    const c = searchParams.get("caffeine");
    if (c === "any") return "contains";
    return isCaffeineFilterMode(c) ? c : "contains";
  }, [searchParams]);

  const setCaffeineMode = (next: CaffeineFilterMode) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next === "contains") nextParams.delete("caffeine");
    else nextParams.set("caffeine", "none");
    setSearchParams(nextParams, { replace: true });
  };

  const includeBottles = useMemo(() => {
    const b = searchParams.get("bottles");
    if (b === "0" || b === "false") return false;
    return true;
  }, [searchParams]);

  const setIncludeBottles = (value: boolean) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value) nextParams.delete("bottles");
    else nextParams.set("bottles", "0");
    setSearchParams(nextParams, { replace: true });
  };

  const currentStep: number = useMemo(() => {
    const raw = searchParams.get("step");
    const n = parseInt(raw ?? "", 10);
    if (Number.isFinite(n) && n >= WIZARD_MIN_STEP && n <= WIZARD_MAX_STEP) {
      return n;
    }
    return WIZARD_MIN_STEP;
  }, [searchParams]);

  const goToStep = useCallback(
    (next: number) => {
      const clamped = Math.max(WIZARD_MIN_STEP, Math.min(WIZARD_MAX_STEP, next));
      const nextParams = new URLSearchParams(searchParams);
      if (clamped === WIZARD_MIN_STEP) nextParams.delete("step");
      else nextParams.set("step", String(clamped));
      setSearchParams(nextParams, { replace: true });
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [searchParams, setSearchParams]
  );

  useLayoutEffect(() => {
    if (urlSyncingRef.current) return;
    const currentStable = energyCalcSearchParamsToStableString(searchParams);
    if (lastSyncedUrlStableRef.current === currentStable) return;
    const sportKey = readSportFromSearchParams(searchParams);
    const h = parseEnergyCalcUrlParams(searchParams, sportKey);

    if (h.skipPresetTimeOnce) skipPresetTimeFromUrlRef.current = true;
    if (h.skipPaceSyncOnce) skipPaceSyncRef.current = true;
    if (h.skipKmhSyncOnce) skipKmhSyncRef.current = true;

    if (h.distancePresetId !== undefined) {
      setDistancePresetId(h.distancePresetId);
    } else {
      setDistancePresetId(defaultDistancePresetId(sportKey));
    }
    if (h.customDistanceKm !== undefined) setCustomDistanceKm(h.customDistanceKm);
    if (h.customTriSwimM !== undefined) setCustomTriSwimM(h.customTriSwimM);
    if (h.customTriBikeKm !== undefined) setCustomTriBikeKm(h.customTriBikeKm);
    if (h.customTriRunKm !== undefined) setCustomTriRunKm(h.customTriRunKm);
    if (h.hours !== undefined) setHours(h.hours);
    if (h.minutes !== undefined) setMinutes(h.minutes);
    if (h.timeSeconds !== undefined) setTimeSeconds(h.timeSeconds);
    if (h.carbsPerHour !== undefined) setCarbsPerHour(h.carbsPerHour);
    if (h.paceMinInput !== undefined) setPaceMinInput(h.paceMinInput);
    if (h.paceSecInput !== undefined) setPaceSecInput(h.paceSecInput);
    if (h.cyclingKmhInput !== undefined) setCyclingKmhInput(h.cyclingKmhInput);
    if (h.triathlonSplitMode !== undefined) setTriathlonSplitMode(h.triathlonSplitMode);
    if (h.triSwimH !== undefined) setTriSwimH(h.triSwimH);
    if (h.triSwimM !== undefined) setTriSwimM(h.triSwimM);
    if (h.triBikeH !== undefined) setTriBikeH(h.triBikeH);
    if (h.triBikeM !== undefined) setTriBikeM(h.triBikeM);
    if (h.triRunH !== undefined) setTriRunH(h.triRunH);
    if (h.triRunM !== undefined) setTriRunM(h.triRunM);
    if (h.personSex !== undefined) setPersonSex(h.personSex);
    if (h.personAgeYears !== undefined) setPersonAgeYears(h.personAgeYears);
    if (h.personHeightCm !== undefined) setPersonHeightCm(h.personHeightCm);
    if (h.personWeightKg !== undefined) setPersonWeightKg(h.personWeightKg);
    if (h.preraceMgPerKg !== undefined) setPreraceMgPerKg(h.preraceMgPerKg);
  }, [searchParams]);

  useEffect(() => {
    if (urlSyncingRef.current) return;
    const snapshot: EnergyCalcUrlSnapshot = {
      sport,
      caffeineMode,
      includeBottles,
      distancePresetId,
      customDistanceKm,
      customTriSwimM,
      customTriBikeKm,
      customTriRunKm,
      hours,
      minutes,
      timeSeconds,
      carbsPerHour,
      paceMinInput,
      paceSecInput,
      cyclingKmhInput,
      triathlonSplitMode,
      triSwimH,
      triSwimM,
      triBikeH,
      triBikeM,
      triRunH,
      triRunM,
      personSex,
      personAgeYears,
      personHeightCm,
      personWeightKg,
      preraceMgPerKg,
    };
    const next = buildEnergyCalcSearchParams(snapshot);
    const existingStep = searchParams.get("step");
    if (existingStep != null && existingStep !== "") {
      next.set("step", existingStep);
    }
    const stable = energyCalcSearchParamsToStableString(next);
    if (lastSyncedUrlStableRef.current === stable) return;
    urlSyncingRef.current = true;
    lastSyncedUrlStableRef.current = stable;
    setSearchParams(next, { replace: true });
    requestAnimationFrame(() => {
      urlSyncingRef.current = false;
    });
  }, [
    sport,
    caffeineMode,
    includeBottles,
    distancePresetId,
    customDistanceKm,
    customTriSwimM,
    customTriBikeKm,
    customTriRunKm,
    hours,
    minutes,
    timeSeconds,
    carbsPerHour,
    paceMinInput,
    paceSecInput,
    cyclingKmhInput,
    triathlonSplitMode,
    triSwimH,
    triSwimM,
    triBikeH,
    triBikeM,
    triRunH,
    triRunM,
    personSex,
    personAgeYears,
    personHeightCm,
    personWeightKg,
    preraceMgPerKg,
    setSearchParams,
  ]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const CUSTOMER_ERR =
      "Kunde inte ladda produktsortimentet just nu. Försök igen om en stund — om problemet kvarstår, kontakta oss.";
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/energy-calculator/catalog`);
        const text = await r.text();
        if (cancelled) return;

        if (!r.ok || text.trim().startsWith("<")) {
          console.error(
            `[EnergyCalc] catalog fetch failed: status=${r.status}`,
            text.slice(0, 300)
          );
          throw new Error(CUSTOMER_ERR);
        }
        const raw = JSON.parse(text) as EnergyCatalog & {
          energySlotConfig?: unknown;
          flavorGroups?: number[][];
          defaultBottleProductId?: number | null;
        };
        const normOne = (p: CalcProduct) => ({
          ...p,
          caffeine: p.caffeine ?? "unknown",
        });
        setCatalog({
          dryck: Array.isArray(raw.dryck) ? raw.dryck.map(normOne) : [],
          gel: Array.isArray(raw.gel) ? raw.gel.map(normOne) : [],
          mat: Array.isArray(raw.mat) ? raw.mat.map(normOne) : [],
          flaskor: Array.isArray(raw.flaskor) ? raw.flaskor.map(normOne) : [],
          prerace: Array.isArray(raw.prerace) ? raw.prerace.map(normOne) : [],
          energySlotConfig: normalizeEnergySlotConfig(raw.energySlotConfig),
          flavorGroups: Array.isArray(raw.flavorGroups) ? raw.flavorGroups : [],
          defaultBottleProductId:
            raw.defaultBottleProductId != null &&
            Number.isFinite(Number(raw.defaultBottleProductId))
              ? Number(raw.defaultBottleProductId)
              : null,
        });
      } catch (e) {
        if (!cancelled) {
          console.error("[EnergyCalc] catalog error:", e);
          setError(
            e instanceof Error && e.message === CUSTOMER_ERR
              ? CUSTOMER_ERR
              : "Kunde inte ladda produktsortimentet just nu. Försök igen om en stund — om problemet kvarstår, kontakta oss."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const triSplit = useMemo(
    () => ({
      swimH: triSwimH,
      swimM: triSwimM,
      bikeH: triBikeH,
      bikeM: triBikeM,
      runH: triRunH,
      runM: triRunM,
    }),
    [triSwimH, triSwimM, triBikeH, triBikeM, triRunH, triRunM]
  );

  const distanceKmNum = useMemo(
    () =>
      resolveRaceDistanceKm(sport, distancePresetId, {
        customKm: customDistanceKm,
        customTriSwimM,
        customTriBikeKm,
        customTriRunKm,
      }),
    [
      sport,
      distancePresetId,
      customDistanceKm,
      customTriSwimM,
      customTriBikeKm,
      customTriRunKm,
    ]
  );

  const distanceCustomMode = distancePresetId === DISTANCE_PRESET_CUSTOM;

  const lockedTriPreset = useMemo(() => {
    if (sport !== "triathlon" || distanceCustomMode) return null;
    return getTriathlonPresetById(distancePresetId) ?? null;
  }, [sport, distanceCustomMode, distancePresetId]);

  const lockedSimpleKm = useMemo(() => {
    if (sport === "triathlon" || distanceCustomMode) return null;
    return getSimpleDistancePresetKm(sport, distancePresetId);
  }, [sport, distanceCustomMode, distancePresetId]);

  const hoursTotal = useMemo(() => {
    if (sport === "triathlon" && triathlonSplitMode) {
      return totalTriathlonHours(triSplit);
    }
    if (sport === "triathlon") {
      return totalHoursFromParts(hours, minutes);
    }
    return totalHoursFromHms(hours, minutes, timeSeconds);
  }, [sport, triathlonSplitMode, triSplit, hours, minutes, timeSeconds]);

  useEffect(() => {
    if (sport !== "running" || distanceKmNum == null || distanceKmNum <= 0) {
      return;
    }
    if (skipPaceSyncRef.current) {
      skipPaceSyncRef.current = false;
      return;
    }
    const ts = hmsToTotalSeconds(hours, minutes, timeSeconds);
    if (ts <= 0) return;
    const secPerKm = ts / distanceKmNum;
    setPaceMinInput(String(Math.floor(secPerKm / 60)));
    setPaceSecInput(String(Math.floor(secPerKm % 60)));
  }, [sport, distanceKmNum, hours, minutes, timeSeconds]);

  useEffect(() => {
    if (sport !== "cycling" || distanceKmNum == null || distanceKmNum <= 0) {
      return;
    }
    if (skipKmhSyncRef.current) {
      skipKmhSyncRef.current = false;
      return;
    }
    const th = totalHoursFromHms(hours, minutes, timeSeconds);
    if (th <= 0) return;
    const kmh = distanceKmNum / th;
    setCyclingKmhInput(String(Math.round(kmh * 10) / 10));
  }, [sport, distanceKmNum, hours, minutes, timeSeconds]);

  /** Sätt tider som matchar vald distansförinställning (marathon ~4 h, 10 km ~1 h, osv.). */
  useEffect(() => {
    if (skipPresetTimeFromUrlRef.current) {
      skipPresetTimeFromUrlRef.current = false;
      return;
    }
    if (distancePresetId === DISTANCE_PRESET_CUSTOM) return;

    if (sport === "triathlon") {
      const t = getTriathlonPresetTimeDefaults(distancePresetId);
      if (!t) return;
      setHours(t.total.hours);
      setMinutes(t.total.minutes);
      setTimeSeconds(0);
      setTriSwimH(t.split.swimH);
      setTriSwimM(t.split.swimM);
      setTriBikeH(t.split.bikeH);
      setTriBikeM(t.split.bikeM);
      setTriRunH(t.split.runH);
      setTriRunM(t.split.runM);
      return;
    }

    const d = getDefaultDurationForSimplePreset(sport, distancePresetId);
    if (!d) return;
    setHours(d.hours);
    setMinutes(d.minutes);
    setTimeSeconds(d.seconds);
  }, [sport, distancePresetId]);

  const bodyWeightKgNum = useMemo(() => {
    if (personWeightKg <= 0) return null;
    return personWeightKg;
  }, [personWeightKg]);

  const preraceCaffeineMg = useMemo(
    () =>
      bodyWeightKgNum != null
        ? preraceCaffeineMgRange(bodyWeightKgNum, preraceMgPerKg)
        : null,
    [bodyWeightKgNum, preraceMgPerKg]
  );

  const bottleCountRecommended = useMemo(
    () =>
      recommendedBottleCount({
        hoursTotal,
        distanceKm: distanceKmNum,
        sport,
        cyclingDistancePresetId:
          sport === "cycling" ? distancePresetId : null,
      }),
    [hoursTotal, distanceKmNum, sport, distancePresetId]
  );

  const targetDuring = useMemo(
    () => targetCarbsDuringRace(hoursTotal, carbsPerHour),
    [hoursTotal, carbsPerHour]
  );

  const preloadCarbs = useMemo(
    () => suggestedPreloadCarbs(targetDuring),
    [targetDuring]
  );

  const filteredCatalog = useMemo(() => {
    if (!catalog) return null;
    return filterCatalogByCaffeine(catalog, caffeineMode);
  }, [catalog, caffeineMode]);

  const raceCarbLines = useMemo(() => {
    if (!filteredCatalog || targetDuring <= 0) return [];
    return sport === "triathlon" && triathlonSplitMode
      ? buildTriathlonSegmentPackageLines(
          filteredCatalog,
          carbsPerHour,
          triSplit
        )
      : buildPackageLines(filteredCatalog, targetDuring, sport);
  }, [
    filteredCatalog,
    targetDuring,
    sport,
    triathlonSplitMode,
    carbsPerHour,
    triSplit,
  ]);

  const hasSportDryckInProposal = useMemo(
    () => packageLinesIncludeSportDryck(raceCarbLines),
    [raceCarbLines]
  );

  const includeBottlesEffective =
    includeBottles && hasSportDryckInProposal;

  const packageLines = useMemo(() => {
    if (!filteredCatalog || targetDuring <= 0) return [];
    if (!includeBottlesEffective) return raceCarbLines;
    const bottleLine = buildBottlePackageLine(
      filteredCatalog,
      bottleCountRecommended
    );
    return bottleLine ? [...raceCarbLines, bottleLine] : raceCarbLines;
  }, [
    filteredCatalog,
    targetDuring,
    raceCarbLines,
    includeBottlesEffective,
    bottleCountRecommended,
  ]);

  const preracePackageLines = useMemo(() => {
    if (!catalog || targetDuring <= 0) return [];
    return buildPreracePackageLines(
      catalog,
      bodyWeightKgNum,
      preraceMgPerKg,
      caffeineMode
    );
  }, [catalog, targetDuring, bodyWeightKgNum, preraceMgPerKg, caffeineMode]);

  const rawCombinedPackageLines = useMemo(
    () => [...preracePackageLines, ...packageLines],
    [preracePackageLines, packageLines]
  );

  /** En rad per gel/bar/st; gel/mat med samma valda produkt omberäknas utifrån samlat kh-mål och kh/st. */
  const combinedPackageLines = useMemo(() => {
    const expanded = expandPackageLinesToSingleUnits(rawCombinedPackageLines);
    if (!catalog) return expanded;
    return rebalanceGelMatExpandedPackageLines(
      expanded,
      flavorChoicesByLineKey,
      catalog
    );
  }, [rawCombinedPackageLines, catalog, flavorChoicesByLineKey]);

  const resolvedCombinedPackageLines = useMemo(() => {
    if (!catalog) return combinedPackageLines;
    return combinedPackageLines.map((line) => {
      const key = packageLineFlavorGroupKey(line);
      const peers = flavorAlternativesForLine(catalog, line, caffeineMode);
      const chosenId = flavorChoicesByLineKey[key];
      const validId =
        chosenId != null && peers.some((p) => p.id === chosenId)
          ? chosenId
          : undefined;
      return resolvePackageLineWithFlavor(line, validId, catalog);
    });
  }, [
    combinedPackageLines,
    catalog,
    flavorChoicesByLineKey,
    caffeineMode,
  ]);

  const { combined: tableCombined, resolved: tableResolved } = useMemo(
    () =>
      splitNonBottlePackageLinesToSingleUnitRows(
        combinedPackageLines,
        resolvedCombinedPackageLines
      ),
    [combinedPackageLines, resolvedCombinedPackageLines]
  );

  const flavorLineKeySet = useMemo(
    () => new Set(tableCombined.map((l) => packageLineFlavorGroupKey(l))),
    [tableCombined]
  );

  useEffect(() => {
    setFlavorChoicesByLineKey((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const k of Object.keys(next)) {
        if (!flavorLineKeySet.has(k)) {
          delete next[k];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [flavorLineKeySet]);

  useEffect(() => {
    setExcludedPackageLineKeys((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const k of Object.keys(next)) {
        if (!flavorLineKeySet.has(k)) {
          delete next[k];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [flavorLineKeySet]);

  const intakeTimeline = useMemo(
    () =>
      buildIntakeTimeline({
        hoursTotal,
        carbsPerHour,
        targetDuring,
        preloadCarbs,
        sport,
        triathlonSplitMode: sport === "triathlon" && triathlonSplitMode,
        triathlonSplit:
          sport === "triathlon" && triathlonSplitMode ? triSplit : null,
        caffeineMode,
        includeBottles: includeBottlesEffective,
        bottleCount: includeBottlesEffective ? bottleCountRecommended : 0,
        resolvedLines: tableResolved,
        bodyWeightKg: bodyWeightKgNum,
        preraceMgPerKg,
        includePreraceLineInCaffeineSum: (i) => {
          const key = packageLineFlavorGroupKey(tableCombined[i]!);
          return !excludedPackageLineKeys[key];
        },
      }),
    [
      hoursTotal,
      carbsPerHour,
      targetDuring,
      preloadCarbs,
      sport,
      triathlonSplitMode,
      triSplit,
      caffeineMode,
      includeBottlesEffective,
      bottleCountRecommended,
      tableResolved,
      bodyWeightKgNum,
      preraceMgPerKg,
      excludedPackageLineKeys,
      tableCombined,
    ]
  );

  const packagePriceTotal = useMemo(
    () =>
      tableResolved.reduce((sum, line, idx) => {
        const key = packageLineFlavorGroupKey(tableCombined[idx]!);
        if (excludedPackageLineKeys[key]) return sum;
        const unit = unitPriceFromProduct(line.product.price);
        return sum + unit * line.units;
      }, 0),
    [tableResolved, tableCombined, excludedPackageLineKeys]
  );

  /** Summa kh från alla markerade rader (samma urval som prisraden). */
  const packageCarbsTotalGrams = useMemo(
    () =>
      tableResolved.reduce((sum, line, idx) => {
        const key = packageLineFlavorGroupKey(tableCombined[idx]!);
        if (excludedPackageLineKeys[key]) return sum;
        return sum + line.carbsDelivered;
      }, 0),
    [tableResolved, tableCombined, excludedPackageLineKeys]
  );

  const includedPackageLineCount = useMemo(
    () =>
      tableCombined.reduce(
        (n, orig) =>
          n + (excludedPackageLineKeys[packageLineFlavorGroupKey(orig)] ? 0 : 1),
        0
      ),
    [tableCombined, excludedPackageLineKeys]
  );

  const applyRunningPaceNumbers = useCallback(
    (pmRaw: number, psRaw: number) => {
      if (!distanceKmNum || distanceKmNum <= 0) return;
      let pm = Math.floor(pmRaw);
      let ps = Math.floor(psRaw);
      if (Number.isNaN(pm)) pm = 0;
      if (Number.isNaN(ps)) ps = 0;
      pm = Math.min(180, Math.max(0, pm));
      ps = Math.min(59, Math.max(0, ps));
      if (pm <= 0 && ps <= 0) return;
      setPaceMinInput(String(pm));
      setPaceSecInput(String(ps));
      skipPaceSyncRef.current = true;
      const total = Math.round(distanceKmNum * (pm * 60 + ps));
      const { h, m, s } = totalSecondsToHms(total);
      setHours(h);
      setMinutes(m);
      setTimeSeconds(s);
    },
    [distanceKmNum]
  );

  const handleRunPaceMinStep = useCallback(
    (n: number) => {
      const ps = Math.min(59, Math.max(0, parseInt(paceSecInput, 10) || 0));
      let pm = n;
      let adjPs = ps;
      if (pm <= 0 && adjPs <= 0) adjPs = 1;
      applyRunningPaceNumbers(pm, adjPs);
    },
    [paceSecInput, applyRunningPaceNumbers]
  );

  const handleRunPaceSecStep = useCallback(
    (n: number) => {
      const pm = Math.min(180, Math.max(0, parseInt(paceMinInput, 10) || 0));
      let ps = n;
      if (pm <= 0 && ps <= 0) ps = 1;
      applyRunningPaceNumbers(pm, ps);
    },
    [paceMinInput, applyRunningPaceNumbers]
  );

  const applyCyclingKmhFromField = () => {
    if (!distanceKmNum || distanceKmNum <= 0) return;
    const v = parseFloat(cyclingKmhInput.replace(",", "."));
    if (!Number.isFinite(v) || v <= 0) return;
    const total = Math.round((distanceKmNum / v) * 3600);
    skipKmhSyncRef.current = true;
    const { h, m, s } = totalSecondsToHms(total);
    setHours(h);
    setMinutes(m);
    setTimeSeconds(s);
  };

  const runPaceMinDisplayed = Math.min(
    180,
    Math.max(0, parseInt(paceMinInput, 10) || 0)
  );
  const runPaceSecDisplayed = Math.min(
    59,
    Math.max(0, parseInt(paceSecInput, 10) || 0)
  );

  const handleAddLinesToCart = () => {
    tableResolved.forEach((line, idx) => {
      const key = packageLineFlavorGroupKey(tableCombined[idx]!);
      if (excludedPackageLineKeys[key]) return;
      const p = line.product;
      addToCart(
        {
          id: (p as { coreId?: number | string }).coreId || p.id,
          sku: (p as { sku?: string }).sku || undefined,
          name: p.name,
          price: parseFloat(p.price) || 0,
          quantity: line.units,
          img: p.image || undefined,
          in_stock: p.inStock,
          stock_status: p.inStock ? "instock" : "outofstock",
          shipping_class: p.shippingClass,
        },
        undefined
      );
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 3000);
  };

  if (loading) return <Loader ariaLabel="Laddar energikalkylator" />;
  if (error) return <ErrorMsg>{error}</ErrorMsg>;

  return (
    <PageWrap>
      <Inner contentWidth={ContentWidth.Wide}>
        <Hero>
          <HeroKicker>Trirebels energikalkylator</HeroKicker>
          <HeroTitle>Bygg din egen raceplan</HeroTitle>
          <HeroLead>
            Berätta vilken sport, distans och tid du siktar på. Vi räknar ut din
            kolhydratbudget och föreslår ett <strong>energipaket</strong> och en{" "}
            <strong>energiplan</strong> som är anpassad till just din dag.
          </HeroLead>
          <PrivacyNote>
            Uppgifterna används bara i din webbläsare för att visa förslag; de sparas inte av oss.
            Dina val synkas i webbadressen så att du kan bokmärka sidan eller
            kopiera länken. Detta ersätter inte individuell näringsrådgivning.
          </PrivacyNote>
        </Hero>

        <StepsBar>
          <WizardSteps
            steps={WIZARD_STEPS.slice()}
            currentStep={currentStep}
            onStepChange={goToStep}
          />
        </StepsBar>

        {currentStep === 1 && (
          <>
            <Card aria-labelledby="ecalc-person-heading">
              <SectionHeading
                id="ecalc-person-heading"
                kicker="Del 1 av 2"
                subtitle="Snabb profil för att räkna kolhydrat- och koffeinmål."
              >
                Om dig
              </SectionHeading>
              <SportBlock>
                <SegmentedControl<"male" | "female">
                  label="Kön"
                  options={[
                    { id: "male", label: "Man" },
                    { id: "female", label: "Kvinna" },
                  ]}
                  value={personSex}
                  onChange={(next) => {
                    if (personSex === next) return;
                    setPersonSex(next);
                    applyPersonProfileForSex(next);
                  }}
                />
              </SportBlock>
              <RunStepperRow>
                <NumberField
                  label="Ålder"
                  value={personAgeYears}
                  min={15}
                  max={90}
                  onChange={setPersonAgeYears}
                />
                <NumberField
                  label="Längd (cm)"
                  value={personHeightCm}
                  min={130}
                  max={220}
                  onChange={setPersonHeightCm}
                />
                <NumberField
                  label="Vikt (kg)"
                  value={personWeightKg}
                  min={35}
                  max={200}
                  onChange={setPersonWeightKg}
                />
              </RunStepperRow>
            </Card>
            <Card aria-labelledby="ecalc-prereq-heading">
              <SectionHeading
                id="ecalc-prereq-heading"
                kicker="Del 2 av 2"
                subtitle="Sport, distans och måltid — grunden för hela kalkylen."
              >
                Dina förutsättningar
              </SectionHeading>
            <SportBlock>
              <SegmentedControl
                label="Typ av sport"
                options={ENERGY_CALC_SPORTS.map<SegmentedOption>((id) => ({
                  id,
                  label: ENERGY_SPORT_LABELS[id],
                }))}
                value={sport}
                onChange={(next) => setSport(next as EnergySport)}
              />
            </SportBlock>
            <section aria-labelledby="ecalc-distance-heading">
              <PrerequisitesSubTitle id="ecalc-distance-heading">Distans</PrerequisitesSubTitle>
              <SegmentedControl
                ariaLabel="Distans"
                options={distancePresetOptions(sport)}
                value={distancePresetId}
                onChange={(optId) => {
                  if (optId === DISTANCE_PRESET_CUSTOM) {
                    if (
                      sport === "triathlon" &&
                      distancePresetId !== DISTANCE_PRESET_CUSTOM
                    ) {
                      const p = getTriathlonPresetById(distancePresetId);
                      if (p) {
                        setCustomTriSwimM(formatPresetNumberForField(p.swimM));
                        setCustomTriBikeKm(formatPresetNumberForField(p.bikeKm));
                        setCustomTriRunKm(formatPresetNumberForField(p.runKm));
                      }
                    } else if (
                      sport !== "triathlon" &&
                      distancePresetId !== DISTANCE_PRESET_CUSTOM
                    ) {
                      const km = getSimpleDistancePresetKm(
                        sport,
                        distancePresetId
                      );
                      if (km != null) {
                        setCustomDistanceKm(formatPresetNumberForField(km));
                      }
                    }
                  } else {
                    if (sport === "triathlon") {
                      const t = getTriathlonPresetTimeDefaults(optId);
                      if (t) {
                        setHours(t.total.hours);
                        setMinutes(t.total.minutes);
                        setTimeSeconds(0);
                        setTriSwimH(t.split.swimH);
                        setTriSwimM(t.split.swimM);
                        setTriBikeH(t.split.bikeH);
                        setTriBikeM(t.split.bikeM);
                        setTriRunH(t.split.runH);
                        setTriRunM(t.split.runM);
                      }
                    } else {
                      const d = getDefaultDurationForSimplePreset(sport, optId);
                      if (d) {
                        setHours(d.hours);
                        setMinutes(d.minutes);
                        setTimeSeconds(d.seconds);
                      }
                    }
                  }
                  skipPresetTimeFromUrlRef.current = false;
                  setDistancePresetId(optId);
                }}
              />
              <DistanceInputsPanel>
                {sport === "triathlon" ? (
                  <TriSplitGrid css={isMobile ? triSplitGridMobile : undefined}>
                    <Field>
                      <FieldLabel htmlFor="ecalc-tri-swim-m">Sim (meter)</FieldLabel>
                      <TextIn
                        id="ecalc-tri-swim-m"
                        type="text"
                        inputMode="decimal"
                        placeholder="t.ex. 1500"
                        disabled={!distanceCustomMode}
                        value={
                          lockedTriPreset
                            ? formatPresetNumberForField(lockedTriPreset.swimM)
                            : customTriSwimM
                        }
                        onChange={(e) => setCustomTriSwimM(e.target.value)}
                      />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="ecalc-tri-bike-km">Cykel (km)</FieldLabel>
                      <TextIn
                        id="ecalc-tri-bike-km"
                        type="text"
                        inputMode="decimal"
                        placeholder="t.ex. 40"
                        disabled={!distanceCustomMode}
                        value={
                          lockedTriPreset
                            ? formatPresetNumberForField(lockedTriPreset.bikeKm)
                            : customTriBikeKm
                        }
                        onChange={(e) => setCustomTriBikeKm(e.target.value)}
                      />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="ecalc-tri-run-km">Löpning (km)</FieldLabel>
                      <TextIn
                        id="ecalc-tri-run-km"
                        type="text"
                        inputMode="decimal"
                        placeholder="t.ex. 10"
                        disabled={!distanceCustomMode}
                        value={
                          lockedTriPreset
                            ? formatPresetNumberForField(lockedTriPreset.runKm)
                            : customTriRunKm
                        }
                        onChange={(e) => setCustomTriRunKm(e.target.value)}
                      />
                    </Field>
                  </TriSplitGrid>
                ) : (
                  <Field>
                    <FieldLabel htmlFor="ecalc-custom-km">Distans (km)</FieldLabel>
                    <TextIn
                      id="ecalc-custom-km"
                      type="text"
                      inputMode="decimal"
                      placeholder="t.ex. 42,2"
                      disabled={!distanceCustomMode}
                      value={
                        lockedSimpleKm != null
                          ? formatPresetNumberForField(lockedSimpleKm)
                          : customDistanceKm
                      }
                      onChange={(e) => setCustomDistanceKm(e.target.value)}
                    />
                  </Field>
                )}
                {distanceKmNum != null && (
                  <DistanceTotalHighlight>
                    Totalt tävlingsdistans: ca{" "}
                    <strong>
                      {distanceKmNum % 1 === 0
                        ? String(Math.round(distanceKmNum))
                        : distanceKmNum.toFixed(1).replace(/\.0$/, "")}{" "}
                      km
                    </strong>
                  </DistanceTotalHighlight>
                )}
                {distanceKmNum == null && distanceCustomMode && (
                  <DistanceTotalMuted>
                    Fyll i alla fält så att total distans kan beräknas.
                  </DistanceTotalMuted>
                )}
              </DistanceInputsPanel>
            </section>

            <PrerequisitesSpacedSubsection aria-labelledby="ecalc-time-heading">
              <PrerequisitesSubTitle id="ecalc-time-heading">Tid</PrerequisitesSubTitle>
              {sport === "triathlon" ? (
                <>
                  <SportBlockLabel>Triathlon: tid</SportBlockLabel>
                  <CheckboxRow>
                    <Checkbox
                      label="Dela tid per gren (sim, cykel, löpning) — olika produktupplägg per delsträcka"
                      checked={triathlonSplitMode}
                      onChange={setTriathlonSplitMode}
                    />
                  </CheckboxRow>
                  {triathlonSplitMode ? (
                    <>
                      <TriSplitHint>
                        Total tid blir summan av grenarna. Kh-takt (g/timme) används per gren för paketförslaget.
                      </TriSplitHint>
                      <TimeInputsPanel>
                        <TriSplitGrid css={isMobile ? triSplitGridMobile : undefined}>
                          <Field>
                            <FieldLabel>Sim</FieldLabel>
                            <TimeRow>
                              <TextIn
                                type="number"
                                min={0}
                                max={48}
                                aria-label="Sim timmar"
                                value={triSwimH}
                                onChange={(e) =>
                                  setTriSwimH(Math.max(0, parseInt(e.target.value, 10) || 0))
                                }
                              />
                              <TimeSep>h</TimeSep>
                              <TextIn
                                type="number"
                                min={0}
                                max={59}
                                aria-label="Sim minuter"
                                value={triSwimM}
                                onChange={(e) =>
                                  setTriSwimM(
                                    Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0))
                                  )
                                }
                              />
                              <TimeSep>min</TimeSep>
                            </TimeRow>
                          </Field>
                          <Field>
                            <FieldLabel>Cykel</FieldLabel>
                            <TimeRow>
                              <TextIn
                                type="number"
                                min={0}
                                max={48}
                                aria-label="Cykel timmar"
                                value={triBikeH}
                                onChange={(e) =>
                                  setTriBikeH(Math.max(0, parseInt(e.target.value, 10) || 0))
                                }
                              />
                              <TimeSep>h</TimeSep>
                              <TextIn
                                type="number"
                                min={0}
                                max={59}
                                aria-label="Cykel minuter"
                                value={triBikeM}
                                onChange={(e) =>
                                  setTriBikeM(
                                    Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0))
                                  )
                                }
                              />
                              <TimeSep>min</TimeSep>
                            </TimeRow>
                          </Field>
                          <Field>
                            <FieldLabel>Löpning</FieldLabel>
                            <TimeRow>
                              <TextIn
                                type="number"
                                min={0}
                                max={48}
                                aria-label="Löpning timmar"
                                value={triRunH}
                                onChange={(e) =>
                                  setTriRunH(Math.max(0, parseInt(e.target.value, 10) || 0))
                                }
                              />
                              <TimeSep>h</TimeSep>
                              <TextIn
                                type="number"
                                min={0}
                                max={59}
                                aria-label="Löpning minuter"
                                value={triRunM}
                                onChange={(e) =>
                                  setTriRunM(
                                    Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0))
                                  )
                                }
                              />
                              <TimeSep>min</TimeSep>
                            </TimeRow>
                          </Field>
                        </TriSplitGrid>
                      </TimeInputsPanel>
                    </>
                  ) : (
                    <TimeInputsPanel>
                      <FieldLabel>Total tid (timmar och minuter)</FieldLabel>
                      <TimeRow>
                        <TextIn
                          type="number"
                          min={0}
                          max={48}
                          aria-label="Timmar"
                          value={hours}
                          onChange={(e) =>
                            setHours(Math.max(0, parseInt(e.target.value, 10) || 0))
                          }
                        />
                        <TimeSep>h</TimeSep>
                        <TextIn
                          type="number"
                          min={0}
                          max={59}
                          aria-label="Minuter"
                          value={minutes}
                          onChange={(e) =>
                            setMinutes(
                              Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0))
                            )
                          }
                        />
                        <TimeSep>min</TimeSep>
                      </TimeRow>
                    </TimeInputsPanel>
                  )}
                </>
              ) : sport === "running" ? (
                <TimeInputsPanel>
                  <RunSubsectionTitle>Total tid</RunSubsectionTitle>
                  <RunSubsectionIntro>
                    Beräknad tid i mål. Ändrar du tiden uppdaterar vi tempot automatiskt — och tvärtom när du
                    justerar tempot. Bra om du vill leka med måltid och fart utifrån vald distans.
                  </RunSubsectionIntro>
                  <RunStepperRow>
                    <NumberField
                      label="Timmar"
                      value={Math.min(48, Math.max(0, hours))}
                      min={0}
                      max={48}
                      onChange={(n) => setHours(n)}
                    />
                    <NumberField
                      label="Minuter"
                      value={Math.min(59, Math.max(0, minutes))}
                      min={0}
                      max={59}
                      onChange={(n) => setMinutes(n)}
                    />
                    <NumberField
                      label="Sekunder"
                      value={Math.min(59, Math.max(0, timeSeconds))}
                      min={0}
                      max={59}
                      onChange={(n) => setTimeSeconds(n)}
                    />
                  </RunStepperRow>
                  <RunSubsectionDivider />
                  <RunSubsectionTitle>Tempo (min/km)</RunSubsectionTitle>
                  <RunStepperRow>
                    <NumberField
                      label="Minuter"
                      value={runPaceMinDisplayed}
                      min={0}
                      max={180}
                      disabled={distanceKmNum == null || distanceKmNum <= 0}
                      onChange={handleRunPaceMinStep}
                    />
                    <NumberField
                      label="Sekunder"
                      value={runPaceSecDisplayed}
                      min={0}
                      max={59}
                      disabled={distanceKmNum == null || distanceKmNum <= 0}
                      onChange={handleRunPaceSecStep}
                    />
                  </RunStepperRow>
                </TimeInputsPanel>
              ) : (
                <TimeInputsPanel>
                  <FieldLabel>Tid (timmar, minuter, sekunder)</FieldLabel>
                  <TimeRow>
                    <TextIn
                      type="number"
                      min={0}
                      max={48}
                      aria-label="Timmar"
                      value={hours}
                      onChange={(e) =>
                        setHours(Math.max(0, parseInt(e.target.value, 10) || 0))
                      }
                    />
                    <TimeSep>h</TimeSep>
                    <TextIn
                      type="number"
                      min={0}
                      max={59}
                      aria-label="Minuter"
                      value={minutes}
                      onChange={(e) =>
                        setMinutes(Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0)))
                      }
                    />
                    <TimeSep>min</TimeSep>
                    <TextIn
                      type="number"
                      min={0}
                      max={59}
                      aria-label="Sekunder"
                      value={timeSeconds}
                      onChange={(e) =>
                        setTimeSeconds(
                          Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0))
                        )
                      }
                    />
                    <TimeSep>s</TimeSep>
                  </TimeRow>
                  {sport === "cycling" && (
                    <>
                      <Field>
                        <FieldLabel htmlFor="ecalc-kmh">Snittfart (km/h)</FieldLabel>
                        <TextIn
                          id="ecalc-kmh"
                          type="text"
                          inputMode="decimal"
                          disabled={distanceKmNum == null}
                          value={cyclingKmhInput}
                          onChange={(e) => setCyclingKmhInput(e.target.value)}
                          onBlur={applyCyclingKmhFromField}
                        />
                      </Field>
                      <TimePaceHint>
                        Hastigheten följer tid och distans. Ändra km/h och lämna fältet för att uppdatera tiden.
                      </TimePaceHint>
                    </>
                  )}
                </TimeInputsPanel>
              )}
            </PrerequisitesSpacedSubsection>

            <ResultBanner>
              <ResultLine>
                <span>Mål under loppet</span>
                <strong>{targetDuring > 0 ? `${targetDuring} g` : "—"} kolhydrater</strong>
              </ResultLine>
              {distanceKmNum != null && (
                <ResultLine>
                  <span>Tävlingsdistans (val)</span>
                  <strong>ca {distanceKmNum.toFixed(1).replace(/\.0$/, "")} km</strong>
                </ResultLine>
              )}
              {targetDuring > 0 && (
                <ResultLine>
                  <span>Raceload (ca före start)</span>
                  <strong>~{preloadCarbs} g</strong>
                </ResultLine>
              )}
              {targetDuring > 0 &&
                includeBottlesEffective &&
                bottleCountRecommended > 0 && (
                <ResultLine>
                  <span>Flaskor (ca)</span>
                  <strong>{bottleCountRecommended} st</strong>
                </ResultLine>
              )}
            </ResultBanner>
            </Card>

            <WizardNav>
              <NavSpacer aria-hidden />
              <Button
                as={Theme.Dark}
                size={Size.Large}
                onClick={() => goToStep(2)}
                disabled={targetDuring <= 0}
              >
                Nästa: Energipaket →
              </Button>
            </WizardNav>
          </>
        )}

        {currentStep > 1 && targetDuring <= 0 && (
          <HintBanner>
            Ange varaktighet i steg 1 innan du går vidare — då räknar vi ut paketförslag och energiplan åt dig.
          </HintBanner>
        )}

        {currentStep === 2 && targetDuring > 0 && (
          <>
            <Card>
              <SectionHeading
                kicker="Steg 2"
                subtitle="Handplockat paket utifrån ditt mål — byt smaker eller ta bort rader du redan har."
              >
                Energipaket (förslag)
              </SectionHeading>
              <SportBlock>
                <SegmentedControl
                  label="Koffein i produktförslaget"
                  options={CAFFEINE_FILTER_MODES.map<SegmentedOption>((id) => ({
                    id,
                    label: CAFFEINE_FILTER_LABELS[id],
                  }))}
                  value={caffeineMode}
                  onChange={(next) => setCaffeineMode(next as CaffeineFilterMode)}
                />
              </SportBlock>
              {caffeineMode === "contains" && (
                <CaffeinePreraceBlock>
                  <SportBlockLabel>Koffein före start (riktlinje)</SportBlockLabel>
                  <SportHint>
                  Välj mängd koffein baserat på kroppsvikt (ca 3–9 mg/kg). Detta används för att räkna ut förslag inför start, i energiplanen och om extra koffein behövs. Anpassa alltid efter individuell tolerans.
                  </SportHint>
                  <CaffeineMgPerKgRow>
                    <Field>
                      <FieldLabel htmlFor="ecalc-prerace-mg">Koffein (mg/kg)</FieldLabel>
                      <TextIn
                        id="ecalc-prerace-mg"
                        type="number"
                        min={PRERACE_MG_PER_KG_MIN}
                        max={PRERACE_MG_PER_KG_MAX}
                        step={1}
                        inputMode="numeric"
                        value={preraceMgPerKg}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          if (Number.isNaN(v)) return;
                          setPreraceMgPerKg(
                            Math.min(PRERACE_MG_PER_KG_MAX, Math.max(PRERACE_MG_PER_KG_MIN, v))
                          );
                        }}
                      />
                    </Field>
                  </CaffeineMgPerKgRow>
                  {preraceCaffeineMg != null && bodyWeightKgNum != null && (
                    <PreraceCaffeineSummary>
                      <strong>ca {preraceCaffeineMg.min} mg totalt</strong> vid {bodyWeightKgNum} kg (
                      {preraceMgPerKg} mg/kg).
                    </PreraceCaffeineSummary>
                  )}
                </CaffeinePreraceBlock>
              )}
              <FieldWide css={{ marginTop: 0 }}>
                <FieldLabel htmlFor="ecalc-gph">
                  Kolhydrater under loppet: {carbsPerHour} g/timme
                </FieldLabel>
                <RangeInput
                  id="ecalc-gph"
                  type="range"
                  min={30}
                  max={120}
                  step={5}
                  value={carbsPerHour}
                  onChange={(e) => setCarbsPerHour(Number(e.target.value))}
                />
                <RangeTicks>
                  <span>30</span>
                  <span>60</span>
                  <span>90</span>
                  <span>120</span>
                </RangeTicks>
              </FieldWide>
              <BottleBlock>
                <CheckboxRow>
                  <Checkbox
                    label={`Inkludera flaskor / vätskesystem i förslaget${
                      !hasSportDryckInProposal && targetDuring > 0
                        ? " (kräver sportdryck i förslaget)"
                        : ""
                    }`}
                    checked={includeBottles}
                    onChange={setIncludeBottles}
                  />
                </CheckboxRow>
              </BottleBlock>

              {combinedPackageLines.length === 0 ? (
                <EmptyCatalog>
                  {catalog &&
                  targetDuring > 0 &&
                  catalogHasAnyUsableProduct(catalog) &&
                  caffeineMode === "none" &&
                  filteredCatalog &&
                  !catalogHasAnyUsableProduct(filteredCatalog) ? (
                    <>
                      Alla kolhydratprodukter i dessa kategorier är markerade med koffein i butiken — inget kunde
                      väljas för &quot;Utan koffein&quot; under loppet. Lägg till koffeinfria poster i WooCommerce
                      eller välj &quot;Med koffein&quot; om du vill se ett förslag.
                    </>
                  ) : (
                    <>
                      Inga produkter med kolhydratdata hittades i dessa kategorier. Kontrollera WooCommerce-meta
                      (carbs_per_serving, servings_per_package eller carbs_per_package).
                    </>
                  )}
                </EmptyCatalog>
              ) : (
                <>
                  <SelectionHint role="note">
                    <strong>Tips:</strong>{" "}
                    {isMobile
                      ? "klicka på ett kort för att ta bort raden — bara markerade kort läggs i varukorgen när du klickar Lägg paket i varukorgen i sista steget."
                      : "bocka ur Med på raderna du redan har hemma — bara markerade rader läggs i varukorgen när du klickar Lägg paket i varukorgen i sista steget."}
                  </SelectionHint>
                  {(() => {
                    type RowWithIndex = {
                      line: (typeof tableResolved)[number];
                      origLine: (typeof tableCombined)[number];
                      idx: number;
                    };
                    const rowsWithIndex: RowWithIndex[] = tableResolved.map(
                      (resolved, idx) => ({
                        line: resolved,
                        origLine: tableCombined[idx]!,
                        idx,
                      })
                    );
                    const preraceGroup = rowsWithIndex.filter(
                      (r) => r.line.preraceIntakeHint != null
                    );
                    const accessoryGroup = rowsWithIndex.filter(
                      (r) =>
                        r.line.preraceIntakeHint == null &&
                        (r.line.accessory === true ||
                          r.line.categoryKey === "flaskor")
                    );
                    const raceGroup = rowsWithIndex.filter(
                      (r) =>
                        r.line.preraceIntakeHint == null &&
                        !(
                          r.line.accessory === true ||
                          r.line.categoryKey === "flaskor"
                        )
                    );

                    const renderCard = ({ line, origLine, idx }: RowWithIndex) => {
                      const flavorKey = packageLineFlavorGroupKey(origLine);
                      const flavorOpts =
                        catalog != null
                          ? flavorAlternativesForLine(catalog, line, caffeineMode)
                          : [];
                      const p = line.product;
                      const isAccessory =
                        line.accessory === true || line.categoryKey === "flaskor";
                      const isPrerace = line.preraceIntakeHint != null;
                      const priorPreraceCaffeineMg = sumPreraceCaffeineMgBeforeIndex(
                        tableResolved,
                        idx,
                        (i) => {
                          const k = packageLineFlavorGroupKey(tableCombined[i]!);
                          return !excludedPackageLineKeys[k];
                        }
                      );
                      const preraceDosingHint = isPrerace
                        ? preraceCaffeineDosingHintSv(
                            p,
                            bodyWeightKgNum,
                            priorPreraceCaffeineMg,
                            preraceMgPerKg
                          )
                        : null;
                      const ul = unitLabelSv(p);
                      const per = effectiveCarbsPerUnit(p);
                      const unitPrice = unitPriceFromProduct(p.price);
                      const lineTotal = unitPrice * line.units;
                      const unitWord =
                        ul === BULK_PURCHASE_UNIT_LABEL_SV && line.units !== 1
                          ? BULK_PURCHASE_UNIT_LABEL_PLURAL_SV
                          : ul === BULK_PURCHASE_UNIT_LABEL_SV
                            ? BULK_PURCHASE_UNIT_LABEL_SV
                            : "st";
                      const lineExcluded = Boolean(
                        excludedPackageLineKeys[flavorKey]
                      );
                      const allocMismatch = carbAllocationMismatchForLine(line);
                      const gelProductChanged =
                        line.categoryKey === "gel" &&
                        line.product.id !== origLine.product.id;
                      const showGelAllocWarning =
                        !isPrerace &&
                        !isAccessory &&
                        gelProductChanged &&
                        !allocMismatch.ok;
                      const isNitrate = p.id === PRERACE_PRODUCT_NITRATE_ID;
                      const caffeinePerServing = p.caffeine_mg_per_serving ?? null;
                      const caffeineTotalMg =
                        caffeinePerServing != null && caffeinePerServing > 0
                          ? Math.round(
                              caffeinePerServing * Math.max(1, line.units)
                            )
                          : null;
                      const carbTagText = isAccessory
                        ? null
                        : isPrerace && line.carbsDelivered <= 0
                          ? null
                          : line.carbsDelivered > 0
                            ? `${line.carbsDelivered} g kh`
                            : per > 0
                              ? `${per} g kh / ${ul}`
                              : null;
                      const toggleIncluded = () => {
                        setExcludedPackageLineKeys((prev) => {
                          const next = { ...prev };
                          if (next[flavorKey]) delete next[flavorKey];
                          else next[flavorKey] = true;
                          return next;
                        });
                      };
                      const cardCss = {
                        ...(lineExcluded ? packageLineCardExcludedCss : {}),
                        ...(showGelAllocWarning ? packageLineCardWarningCss : {}),
                      };
                      return (
                        <PackageLineCard
                          key={
                            origLine.lineId
                              ? `${origLine.lineId}-${flavorKey}`
                              : `${origLine.categoryKey}-${origLine.product.id}-${idx}`
                          }
                          role="button"
                          tabIndex={0}
                          aria-pressed={!lineExcluded}
                          aria-label={
                            lineExcluded
                              ? `Ta med ${p.name} i paketet`
                              : `Ta bort ${p.name} från paketet`
                          }
                          css={cardCss}
                          onClick={toggleIncluded}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleIncluded();
                            }
                          }}
                        >
                          <CardTopRow>
                            <CategoryBadge>
                              {isPrerace
                                ? `${line.categoryLabel} · ${line.preraceIntakeHint}`
                                : line.categoryLabel}
                            </CategoryBadge>
                            <IncludeIndicator aria-hidden>
                              {lineExcluded ? (
                                <ExcludedIndicator>Ej med</ExcludedIndicator>
                              ) : (
                                <IncludedBadge>
                                  <GrCheckmark />
                                </IncludedBadge>
                              )}
                            </IncludeIndicator>
                          </CardTopRow>
                          <CardMediaRow>
                            {p.image ? (
                              <CardThumb src={p.image} alt="" />
                            ) : (
                              <CardThumbPlaceholder />
                            )}
                            <CardContent>
                              <Link
                                to={`/product/${p.slug}`}
                                style={productLinkOuter}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <CardProductName>{p.name}</CardProductName>
                              </Link>
                              {(carbTagText != null ||
                                caffeineTotalMg != null ||
                                isNitrate) && (
                                <CardTagRow>
                                  {carbTagText != null && (
                                    <CardCarbTag>{carbTagText}</CardCarbTag>
                                  )}
                                  {caffeineTotalMg != null && (
                                    <CardCafTag>
                                      {caffeineTotalMg} mg koffein
                                    </CardCafTag>
                                  )}
                                  {isNitrate && (
                                    <CardNitrateTag>Nitrat</CardNitrateTag>
                                  )}
                                </CardTagRow>
                              )}
                              {isPrerace && preraceDosingHint ? (
                                <CardHintNote>{preraceDosingHint}</CardHintNote>
                              ) : null}
                              {isAccessory && (
                                <CardHintNote>
                                  Vätskeflaska — ingår ej i kolhydratmålet
                                </CardHintNote>
                              )}
                              {!p.inStock && (
                                <CardStockNote>Slut i lager</CardStockNote>
                              )}
                              {showGelAllocWarning && (
                                <CardMismatch>
                                  {allocMismatch.delta > 0 &&
                                  per > 0 &&
                                  per < line.allocationTarget &&
                                  line.units >= 2 ? (
                                    <>
                                      En gel har {per} g kh mot radmål ca{" "}
                                      {line.allocationTarget} g. För att nå målet
                                      behövs {line.units} st → {line.carbsDelivered} g
                                      (+{allocMismatch.delta} g över målet).
                                    </>
                                  ) : allocMismatch.delta > 0 ? (
                                    <>
                                      Radmål ca {line.allocationTarget} g kh,
                                      levererat {line.carbsDelivered} g (+
                                      {allocMismatch.delta} g).
                                    </>
                                  ) : (
                                    <>
                                      Radmål ca {line.allocationTarget} g kh,
                                      levererat {line.carbsDelivered} g (
                                      {allocMismatch.delta} g).
                                    </>
                                  )}
                                </CardMismatch>
                              )}
                            </CardContent>
                          </CardMediaRow>
                          {flavorOpts.length > 1 && (
                            <CardFlavorRow>
                              <CardFlavorSelect
                                aria-label={`Byt produkt: ${line.categoryLabel}`}
                                value={String(p.id)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  const id = Number(e.target.value);
                                  if (!Number.isFinite(id)) return;
                                  setFlavorChoicesByLineKey((prev) => ({
                                    ...prev,
                                    [flavorKey]: id,
                                  }));
                                }}
                              >
                                {flavorOpts.map((opt) => (
                                  <option key={opt.id} value={String(opt.id)}>
                                    {opt.name}
                                  </option>
                                ))}
                              </CardFlavorSelect>
                            </CardFlavorRow>
                          )}
                          <CardFooter>
                            <CardFooterMeta>
                              {isAccessory
                                ? `${line.units} st`
                                : `${line.units} ${unitWord}`}
                              {unitPrice > 0
                                ? ` · ${formatPriceKr(unitPrice)}/st`
                                : ""}
                            </CardFooterMeta>
                            <CardLineTotal>
                              {formatPriceKr(lineTotal)}
                            </CardLineTotal>
                          </CardFooter>
                        </PackageLineCard>
                      );
                    };

                    const renderRow = ({ line, origLine, idx }: RowWithIndex) => {
                      const flavorKey = packageLineFlavorGroupKey(origLine);
                      const flavorOpts =
                        catalog != null
                          ? flavorAlternativesForLine(catalog, line, caffeineMode)
                          : [];
                      const p = line.product;
                      const isAccessory =
                        line.accessory === true || line.categoryKey === "flaskor";
                      const isPrerace = line.preraceIntakeHint != null;
                      const priorPreraceCaffeineMg = sumPreraceCaffeineMgBeforeIndex(
                        tableResolved,
                        idx,
                        (i) => {
                          const k = packageLineFlavorGroupKey(tableCombined[i]!);
                          return !excludedPackageLineKeys[k];
                        }
                      );
                      const preraceDosingHint = isPrerace
                        ? preraceCaffeineDosingHintSv(
                            p,
                            bodyWeightKgNum,
                            priorPreraceCaffeineMg,
                            preraceMgPerKg
                          )
                        : null;
                      const ul = unitLabelSv(p);
                      const per = effectiveCarbsPerUnit(p);
                      const unitPrice = unitPriceFromProduct(p.price);
                      const lineTotal = unitPrice * line.units;
                      const unitWord =
                        ul === BULK_PURCHASE_UNIT_LABEL_SV && line.units !== 1
                          ? BULK_PURCHASE_UNIT_LABEL_PLURAL_SV
                          : ul === BULK_PURCHASE_UNIT_LABEL_SV
                            ? BULK_PURCHASE_UNIT_LABEL_SV
                            : "st";
                      const lineExcluded = Boolean(
                        excludedPackageLineKeys[flavorKey]
                      );
                      const allocMismatch = carbAllocationMismatchForLine(line);
                      const gelProductChanged =
                        line.categoryKey === "gel" &&
                        line.product.id !== origLine.product.id;
                      const showGelAllocWarning =
                        !isPrerace &&
                        !isAccessory &&
                        gelProductChanged &&
                        !allocMismatch.ok;
                      const rowHighlight =
                        showGelAllocWarning && !lineExcluded
                          ? {
                              backgroundColor: "rgba(243, 156, 18, 0.12)",
                              boxShadow: `inset 3px 0 0 0 ${orange}`,
                            }
                          : showGelAllocWarning && lineExcluded
                            ? {
                                backgroundColor: "rgba(243, 156, 18, 0.07)",
                                boxShadow: `inset 3px 0 0 0 ${orange}`,
                              }
                            : undefined;
                      return (
                        <tr
                          key={
                            origLine.lineId
                              ? `${origLine.lineId}-${flavorKey}`
                              : `${origLine.categoryKey}-${origLine.product.id}-${idx}`
                          }
                          style={
                            lineExcluded
                              ? { opacity: 0.48, ...rowHighlight }
                              : rowHighlight
                          }
                        >
                          <TdInclude>
                            <LineIncludeCheck
                              type="checkbox"
                              checked={!lineExcluded}
                              onChange={() => {
                                setExcludedPackageLineKeys((prev) => {
                                  const next = { ...prev };
                                  if (next[flavorKey]) delete next[flavorKey];
                                  else next[flavorKey] = true;
                                  return next;
                                });
                              }}
                              aria-label={
                                lineExcluded
                                  ? `Ta med ${line.categoryLabel} i köp`
                                  : `Ta bort ${line.categoryLabel} från köp`
                              }
                            />
                          </TdInclude>
                          <Td>{line.categoryLabel}</Td>
                          <Td>
                            <ProductCell>
                              {p.image && <MiniThumb src={p.image} alt="" />}
                              <ProductMeta>
                                <Link
                                  to={`/product/${p.slug}`}
                                  style={productLinkOuter}
                                >
                                  <ProductNameLink>{p.name}</ProductNameLink>
                                </Link>
                                {flavorOpts.length > 1 && (
                                  <FlavorSelect
                                    aria-label={`Byt produkt: ${line.categoryLabel}`}
                                    value={String(p.id)}
                                    onChange={(e) => {
                                      const id = Number(e.target.value);
                                      if (!Number.isFinite(id)) return;
                                      setFlavorChoicesByLineKey((prev) => ({
                                        ...prev,
                                        [flavorKey]: id,
                                      }));
                                    }}
                                  >
                                    {flavorOpts.map((opt) => (
                                      <option key={opt.id} value={String(opt.id)}>
                                        {opt.name}
                                      </option>
                                    ))}
                                  </FlavorSelect>
                                )}
                                <CarbHint>
                                  {isPrerace ? (
                                    <>
                                      Intas {line.preraceIntakeHint}.
                                      {per > 0 ? (
                                        <>
                                          {" "}
                                          {per} g / {ul}
                                        </>
                                      ) : null}
                                      {!p.inStock && " · slut i lager"}
                                      {preraceDosingHint
                                        ? ` · ${preraceDosingHint}`
                                        : ""}
                                    </>
                                  ) : isAccessory ? (
                                    <>
                                      Vätskeflaska — ingår ej i kolhydratmålet
                                      {!p.inStock && " · slut i lager"}
                                    </>
                                  ) : (
                                    <>
                                      {per} g / {ul}
                                      {!p.inStock && " · slut i lager"}
                                    </>
                                  )}
                                </CarbHint>
                                {showGelAllocWarning ? (
                                  <CarbMismatchNote>
                                    {allocMismatch.delta > 0 &&
                                    per > 0 &&
                                    per < line.allocationTarget &&
                                    line.units >= 2 ? (
                                      <>
                                        En gel har {per} g kh mot radmål ca{" "}
                                        {line.allocationTarget} g (
                                        {per - line.allocationTarget} g per gel).
                                        För att nå minst radmålet behövs{" "}
                                        {line.units} st → {line.carbsDelivered} g
                                        totalt (+{allocMismatch.delta} g över
                                        målet).
                                      </>
                                    ) : allocMismatch.delta > 0 ? (
                                      <>
                                        Radmål ca {line.allocationTarget} g kh,
                                        levererat {line.carbsDelivered} g (+
                                        {allocMismatch.delta} g). Välj annan gel
                                        eller kryssa ur raden om du vill komma
                                        närmare målet.
                                      </>
                                    ) : (
                                      <>
                                        Radmål ca {line.allocationTarget} g kh,
                                        levererat {line.carbsDelivered} g (
                                        {allocMismatch.delta} g). Kontrollera vald
                                        gel eller produktdata.
                                      </>
                                    )}
                                  </CarbMismatchNote>
                                ) : null}
                              </ProductMeta>
                            </ProductCell>
                          </Td>
                          <Td>
                            {isAccessory
                              ? `${line.units} st`
                              : `${line.units} ${unitWord}`}
                          </Td>
                          <Td>
                            {isAccessory
                              ? "—"
                              : isPrerace && line.carbsDelivered <= 0
                                ? "—"
                                : `${line.carbsDelivered} g`}
                          </Td>
                          <TdNum>{formatPriceKr(unitPrice)}</TdNum>
                          <TdNum>{formatPriceKr(lineTotal)}</TdNum>
                        </tr>
                      );
                    };

                    if (!isMobile) {
                      return (
                        <TableWrap>
                          <Table>
                            <thead>
                              <tr>
                                <ThInclude scope="col">Med</ThInclude>
                                <Th>Kategori</Th>
                                <Th>Produkt</Th>
                                <Th>Antal</Th>
                                <Th>ca kh</Th>
                                <ThNum>À-pris</ThNum>
                                <ThNum>Summa</ThNum>
                              </tr>
                            </thead>
                            <tbody>{rowsWithIndex.map(renderRow)}</tbody>
                            <tfoot>
                              <tr>
                                <TotalLabelCell colSpan={4}>
                                  Totalt för paketförslaget (markerade rader)
                                </TotalLabelCell>
                                <TotalTdNum>
                                  <strong>{packageCarbsTotalGrams} g</strong>
                                </TotalTdNum>
                                <TotalTdNum>—</TotalTdNum>
                                <TotalValueCell>
                                  {formatPriceKr(packagePriceTotal)}
                                </TotalValueCell>
                              </tr>
                            </tfoot>
                          </Table>
                        </TableWrap>
                      );
                    }

                    return (
                      <>
                        {preraceGroup.length > 0 && (
                          <PackageGroup>
                            <PackageGroupHeader>
                              <PackageGroupTitle>Före start</PackageGroupTitle>
                              <PackageGroupSubtitle>
                                Raceload och koffein inför starten.
                              </PackageGroupSubtitle>
                            </PackageGroupHeader>
                            <PackageGrid>
                              {preraceGroup.map(renderCard)}
                            </PackageGrid>
                          </PackageGroup>
                        )}
                        {raceGroup.length > 0 && (
                          <PackageGroup>
                            <PackageGroupHeader>
                              <PackageGroupTitle>Under loppet</PackageGroupTitle>
                              <PackageGroupSubtitle>
                                Det här intar du löpande under själva tävlingen.
                              </PackageGroupSubtitle>
                            </PackageGroupHeader>
                            <PackageGrid>{raceGroup.map(renderCard)}</PackageGrid>
                          </PackageGroup>
                        )}
                        {accessoryGroup.length > 0 && (
                          <PackageGroup>
                            <PackageGroupHeader>
                              <PackageGroupTitle>Tillbehör</PackageGroupTitle>
                              <PackageGroupSubtitle>
                                Flaskor och övrigt — ingår ej i kolhydratmålet.
                              </PackageGroupSubtitle>
                            </PackageGroupHeader>
                            <PackageGrid>
                              {accessoryGroup.map(renderCard)}
                            </PackageGrid>
                          </PackageGroup>
                        )}
                        <PackageTotalsRow>
                          <PackageTotalsLabel>
                            Totalt för paketförslaget (markerade rader)
                          </PackageTotalsLabel>
                          <PackageTotalsValues>
                            <PackageTotalsCarbs>
                              {packageCarbsTotalGrams} g kh
                            </PackageTotalsCarbs>
                            <PackageTotalsPrice>
                              {formatPriceKr(packagePriceTotal)}
                            </PackageTotalsPrice>
                          </PackageTotalsValues>
                        </PackageTotalsRow>
                      </>
                    );
                  })()}
                </>
              )}
            </Card>

            <WizardNav>
              <Button
                as={Theme.Dark}
                size={Size.Large}
                onClick={() => goToStep(1)}
              >
                ← Tillbaka
              </Button>
              <Button
                as={Theme.Dark}
                size={Size.Large}
                onClick={() => goToStep(3)}
              >
                Nästa: Energiplan →
              </Button>
            </WizardNav>
          </>
        )}

        {currentStep === 3 && targetDuring > 0 && (
          <>
            <Card>
              <SectionHeading
                kicker="Steg 3"
                subtitle="Så här fördelar du intaget i realtid under loppet."
              >
                Energiplan
              </SectionHeading>
              {intakeTimeline ? (
                <IntakePlanTimeline
                  data={intakeTimeline}
                  topSlot={
                    sport === "cycling" && intakeTimeline.cyclingPlan != null ? (
                      <CyclingBottlePlanner
                        totalDryckG={intakeTimeline.cyclingPlan.totalDryckG}
                        distanceKm={distanceKmNum}
                        bottleCount={2}
                        useOwnStops={useCyclingOwnStops}
                        onUseOwnStopsChange={setUseCyclingOwnStops}
                        stopsKm={cyclingStopsKm}
                        onStopsKmChange={setCyclingStopsKm}
                        suggestedStops={
                          distancePresetId === "vattern315"
                            ? [
                                { km: 133, label: "Fagerhult" },
                                { km: 204, label: "Karlsborg" },
                              ]
                            : []
                        }
                      />
                    ) : null
                  }
                />
              ) : null}
            </Card>

            <CartSummaryHint role="note">
              {includedPackageLineCount > 0 ? (
                <>
                  <strong>{includedPackageLineCount}</strong> markerade{" "}
                  {includedPackageLineCount === 1 ? "rad" : "rader"} från paketförslaget läggs i varukorgen. Behöver du justera något? Gå tillbaka till <em>Steg 2 · Energipaket</em>.
                </>
              ) : (
                <>
                  Inga rader är markerade just nu — gå tillbaka till <em>Steg 2 · Energipaket</em> och bocka i det du vill ha med i varukorgen.
                </>
              )}
            </CartSummaryHint>

            <WizardNav>
              <Button
                as={Theme.Dark}
                size={Size.Large}
                onClick={() => goToStep(2)}
              >
                ← Tillbaka
              </Button>
              <Button
                as={added ? Theme.Positive : Theme.Dark}
                size={Size.Large}
                onClick={handleAddLinesToCart}
                disabled={
                  combinedPackageLines.length === 0 ||
                  includedPackageLineCount === 0
                }
              >
                {added
                  ? "Tillagt i varukorgen!"
                  : includedPackageLineCount === 0
                    ? "Markera minst en rad i steg 2"
                    : "Lägg paket i varukorgen"}
              </Button>
            </WizardNav>
          </>
        )}

        {currentStep === 1 && targetDuring <= 0 && (
          <HintBanner>
            Ange varaktighet (och dra reglaget för kh/timme) för att se energiplan och energipaketförslag.
          </HintBanner>
        )}
      </Inner>
    </PageWrap>
  );
};

export default EnergyCalculatorPage;

const PageWrap = styled.div({
  padding: { xy: spacing.xxxl },
  backgroundColor: white,
  ...media({ maxWidth: "767px" }, {
    padding: { x: spacing.m, y: spacing.xl },
  }),
});

const Inner = styled(Base, {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xl,
});

/** Hero-block med accentfärg för att signalera Trirebels-identitet. */
const Hero = styled.header({
  position: "relative",
  padding: { x: spacing.xxl, y: spacing.xxl },
  borderRadius: "16px",
  color: white,
  backgroundColor: black,
  backgroundImage: `linear-gradient(135deg, ${black} 0%, ${darkGreen} 78%, ${green} 100%)`,
  overflow: "hidden",
  ...media({ maxWidth: "767px" }, {
    padding: { x: spacing.l, y: spacing.xl },
    borderRadius: "12px",
  }),
});

const HeroKicker = styled.span({
  display: "inline-block",
  fontSize: fontSize.xs,
  fontWeight: fontWeight.heavy,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: white,
  backgroundColor: "rgba(255,255,255,0.12)",
  padding: { x: spacing.s, y: "6px" },
  borderRadius: "999px",
  marginBottom: spacing.m,
});

const HeroTitle = styled.h1({
  fontSize: fontSize.hero,
  fontWeight: fontWeight.heavy,
  lineHeight: 1.05,
  color: white,
  margin: { xy: 0 },
  marginBottom: spacing.m,
  maxWidth: "820px",
  ...media({ maxWidth: "767px" }, {
    fontSize: fontSize.xxl,
  }),
});

const HeroLead = styled.p({
  fontSize: fontSize.ml,
  lineHeight: "1.6",
  color: white,
  maxWidth: "760px",
  margin: { xy: 0 },
  marginBottom: spacing.m,
  ...media({ maxWidth: "767px" }, {
    fontSize: fontSize.m,
  }),
});

const PrivacyNote = styled.p({
  fontSize: fontSize.s,
  color: "rgba(255,255,255,0.85)",
  fontStyle: "italic",
  maxWidth: "760px",
  margin: { xy: 0 },
});

/** Genomgående kort-mönster för alla sektioner. */
const Card = styled.section({
  backgroundColor: mediumGrey,
  borderRadius: "12px",
  padding: { xy: spacing.l },
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  ...media({ maxWidth: "767px" }, {
    padding: { xy: spacing.m },
    borderRadius: "10px",
  }),
});

/** Container för wizard-step-raden precis under hero. */
const StepsBar = styled.div({
  backgroundColor: white,
  borderRadius: "12px",
  padding: { x: spacing.l, y: spacing.m },
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  marginBottom: spacing.m,
  ...media({ maxWidth: "767px" }, {
    padding: { x: spacing.s, y: spacing.s },
    borderRadius: "10px",
  }),
});

/** Navigeringsrad med Tillbaka / Nästa-knappar under varje wizard-steg. */
const WizardNav = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.m,
  padding: { x: 0, y: spacing.m },
  flexWrap: "wrap",
  ...media({ maxWidth: "520px" }, {
    flexDirection: "column-reverse",
    alignItems: "stretch",
  }),
});

/** Används när första stegets nav bara har en Nästa-knapp och behöver push:a den åt höger. */
const NavSpacer = styled.div({
  flex: { grow: "1", shrink: "0", basis: "0" },
});

/** Informationsbanner ovanför paket-tabellen som förklarar kryssrutans betydelse. */
const SelectionHint = styled.p({
  margin: { top: 0, right: 0, bottom: spacing.m, left: 0 },
  padding: { x: spacing.m, y: spacing.s },
  backgroundColor: white,
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  borderLeft: { style: "solid", color: black, width: "3px" },
  borderRadius: "8px",
  fontSize: fontSize.s,
  color: darkGrey,
  lineHeight: "1.45",
});

/** Sammanfattningsruta i sista steget som visar hur många rader som läggs i varukorgen. */
const CartSummaryHint = styled.p({
  margin: { top: spacing.m, right: 0, bottom: 0, left: 0 },
  padding: { x: spacing.m, y: spacing.s },
  backgroundColor: white,
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  borderLeft: { style: "solid", color: black, width: "3px" },
  borderRadius: "8px",
  fontSize: fontSize.s,
  color: darkGrey,
  lineHeight: "1.45",
});

/** Underrubrik inuti förutsättningar, t.ex. Distans och Tid. */
const PrerequisitesSubTitle = styled.h3({
  fontSize: fontSize.l,
  fontWeight: fontWeight.bold,
  margin: { top: 0, right: 0, bottom: spacing.s, left: 0 },
  color: black,
});

const PrerequisitesSpacedSubsection = styled.section({
  marginTop: spacing.xl,
});

const TimeInputsPanel = styled.div({
  marginTop: spacing.m,
  padding: { xy: spacing.m },
  backgroundColor: white,
  borderRadius: "8px",
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
});

const TimePaceHint = styled.p({
  fontSize: fontSize.xxs,
  color: darkGrey,
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  lineHeight: "1.5",
});

const DistanceInputsPanel = styled.div({
  marginTop: spacing.m,
  padding: { xy: spacing.m },
  backgroundColor: white,
  borderRadius: "8px",
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
});

const DistanceTotalHighlight = styled.div({
  fontSize: fontSize.m,
  color: darkGrey,
  textAlign: "center",
  marginTop: spacing.s,
  paddingTop: spacing.m,
  borderTop: { style: "solid", color: grey, width: "1px" },
});

const DistanceTotalMuted = styled.p({
  fontSize: fontSize.xxs,
  color: darkGrey,
  fontStyle: "italic",
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  textAlign: "center",
});

const SportBlock = styled.div({
  marginBottom: spacing.l,
});

const SportBlockLabel = styled.span({
  display: "block",
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: darkGrey,
  marginBottom: spacing.xs,
});

const CheckboxRow = styled.div({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: spacing.s,
  marginBottom: spacing.s,
});

const SportHint = styled.p({
  fontSize: fontSize.xxs,
  color: darkGrey,
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  lineHeight: "1.45",
});

const TriSplitHint = styled.p({
  fontSize: fontSize.xxs,
  color: darkGrey,
  margin: { top: 0, right: 0, bottom: spacing.s, left: 0 },
  lineHeight: "1.45",
});

const triSplitGridMobile = { gridTemplateColumns: "1fr" as const };

const TriSplitGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: spacing.m,
});

const Field = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
});

const FieldWide = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  marginTop: spacing.m,
});

const CaffeinePreraceBlock = styled.div({
  marginTop: spacing.m,
  marginBottom: spacing.m,
  padding: { xy: spacing.m },
  backgroundColor: white,
  borderRadius: "8px",
  border: { xy: { style: "solid", color: grey, width: "1px" } },
});

const CaffeineMgPerKgRow = styled.div({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: spacing.m,
  marginTop: spacing.s,
  alignItems: "flex-end",
});

const PreraceCaffeineSummary = styled.p({
  fontSize: fontSize.s,
  color: darkGrey,
  margin: { top: spacing.m, right: 0, bottom: 0, left: 0 },
  lineHeight: "1.5",
});

const BottleBlock = styled.div({
  marginTop: spacing.m,
  marginBottom: spacing.l,
});

const FieldLabel = styled.label({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: darkGrey,
});

const TextIn = styled.input({
  padding: { xy: spacing.s },
  fontSize: fontSize.m,
  border: { xy: { style: "solid", color: black, width: "1px" } },
  borderRadius: "4px",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: white,
  color: black,
  transitionProperty: "border-color",
  transitionDuration: "0.2s",
  transitionTimingFunction: "ease",
  ":focus": {
    outline: { style: "none" },
    border: { xy: { style: "solid", color: green, width: "1px" } },
  },
  ":disabled": {
    backgroundColor: mediumGrey,
    color: darkGrey,
    cursor: "not-allowed",
  },
});

const TimeRow = styled.div({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xs,
});

const TimeSep = styled.span({
  fontSize: fontSize.s,
  color: darkGrey,
  flexShrink: 0,
});

const RangeInput = styled.input({
  width: "100%",
  marginTop: spacing.xs,
  accentColor: green,
});

const RangeTicks = styled.div({
  display: "flex",
  justifyContent: "space-between",
  fontSize: fontSize.xxs,
  color: darkGrey,
  marginTop: spacing.xxs,
});

const ResultBanner = styled.div({
  marginTop: spacing.l,
  padding: { xy: spacing.m },
  backgroundColor: white,
  borderRadius: "8px",
  border: { xy: { style: "solid", color: grey, width: "1px" } },
});

const ResultLine = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  fontSize: fontSize.m,
  marginBottom: spacing.xs,
  ":last-child": { marginBottom: 0 },
});

const productLinkOuter: React.CSSProperties = {
  textDecoration: "none",
};

/**
 * Gruppering av paketrader per fas (Före start / Under loppet / Tillbehör).
 * Varje grupp har en rubrik + subtitle och ett responsivt grid.
 */
const PackageGroup = styled.section({
  marginBottom: spacing.l,
  ":last-of-type": { marginBottom: spacing.m },
});

const PackageGroupHeader = styled.div({
  marginBottom: spacing.s,
});

const PackageGroupTitle = styled.h3({
  margin: { xy: 0 },
  fontSize: fontSize.m,
  fontWeight: fontWeight.heavy,
  color: black,
  letterSpacing: "0.02em",
});

const PackageGroupSubtitle = styled.p({
  margin: { top: "2px", right: 0, bottom: 0, left: 0 },
  fontSize: fontSize.xs,
  color: darkGrey,
  lineHeight: 1.45,
});

/**
 * Responsivt grid — 2 kolumner på desktop (tät layout utan horisontell scroll),
 * 1 kolumn på mobil så varje kort får hela bredden.
 */
const PackageGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: spacing.s,
  ...media({ maxWidth: "767px" }, {
    gridTemplateColumns: "1fr",
  }),
});

/**
 * Hela kortet är en "knapp" som togglar om raden ingår i köpet.
 * Vi styr visuellt läge via `data-included` och `data-warning`.
 */
const PackageLineCard = styled.div({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  padding: { xy: spacing.s },
  backgroundColor: white,
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  borderRadius: "10px",
  cursor: "pointer",
  transitionProperty: "box-shadow, transform, border-color, background-color",
  transitionDuration: "120ms",
  transitionTimingFunction: "ease",
  userSelect: "none",
  ":hover": {
    border: { xy: { style: "solid", color: black, width: "1px" } },
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  ":focus-visible": {
    outline: { style: "solid", color: black, width: "2px" },
  },
});

const packageLineCardExcludedCss = {
  opacity: 0.55,
  backgroundColor: lightGrey,
  border: { xy: { style: "dashed" as const, color: grey, width: "1px" } },
};

const packageLineCardWarningCss = {
  borderLeft: { style: "solid" as const, color: orange, width: "4px" },
};

const CardTopRow = styled.div({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.xs,
  marginBottom: spacing.xs,
});

const CategoryBadge = styled.span({
  display: "inline-block",
  fontSize: fontSize.xxs,
  fontWeight: fontWeight.heavy,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: darkGrey,
});

const IncludeIndicator = styled.div({
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
});

const IncludedBadge = styled.span({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "22px",
  height: "22px",
  borderRadius: "999px",
  backgroundColor: green,
  color: white,
  fontSize: "11px",
});

const ExcludedIndicator = styled.span({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: { x: spacing.xs, y: "2px" },
  borderRadius: "999px",
  border: { xy: { style: "dashed", color: darkGrey, width: "1px" } },
  fontSize: fontSize.xxs,
  fontWeight: fontWeight.bold,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: darkGrey,
  backgroundColor: white,
});

const CardMediaRow = styled.div({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: spacing.s,
  marginBottom: spacing.s,
});

const CardThumb = styled.img({
  width: "56px",
  height: "56px",
  objectFit: "contain",
  borderRadius: "6px",
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  backgroundColor: white,
  flexShrink: 0,
});

const CardThumbPlaceholder = styled.div({
  width: "56px",
  height: "56px",
  borderRadius: "6px",
  backgroundColor: mediumGrey,
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  flexShrink: 0,
});

const CardContent = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  minWidth: 0,
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: "auto",
});

const CardProductName = styled.span({
  display: "block",
  fontSize: fontSize.s,
  fontWeight: fontWeight.heavy,
  color: black,
  lineHeight: 1.25,
  ":hover": {
    textDecoration: "underline",
    color: darkGreen,
  },
});

const CardTagRow = styled.div({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: "4px",
  marginTop: "2px",
});

const cardTagBase = {
  display: "inline-block" as const,
  fontSize: "10px",
  fontWeight: fontWeight.bold,
  textTransform: "uppercase" as const,
  letterSpacing: "0.04em",
  padding: { x: "6px", y: "2px" },
  borderRadius: "3px",
};

const CardCarbTag = styled.div({
  ...cardTagBase,
  backgroundColor: green,
  color: white,
});

const CardCafTag = styled.div({
  ...cardTagBase,
  backgroundColor: orange,
  color: black,
});

const CardNitrateTag = styled.div({
  ...cardTagBase,
  backgroundColor: "#0073E6",
  color: white,
});

const CardFlavorRow = styled.div({
  marginTop: spacing.s,
});

const CardFlavorSelect = styled.select({
  fontSize: fontSize.xs,
  width: "100%",
  display: "block",
  padding: { top: "8px", right: spacing.s, bottom: "8px", left: spacing.s },
  borderRadius: "8px",
  border: { xy: { style: "solid", color: black, width: "1px" } },
  backgroundColor: white,
  color: black,
  cursor: "pointer",
  ":focus": {
    outline: { style: "none" },
    border: { xy: { style: "solid", color: green, width: "1px" } },
  },
});

const CardHintNote = styled.span({
  display: "block",
  marginTop: spacing.xxs,
  fontSize: fontSize.xs,
  color: darkGrey,
  lineHeight: 1.4,
});

const CardStockNote = styled.span({
  display: "inline-block",
  marginTop: spacing.xxs,
  fontSize: fontSize.xxs,
  fontWeight: fontWeight.bold,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: darkRed,
});

const CardMismatch = styled.span({
  display: "block",
  marginTop: spacing.xxs,
  fontSize: fontSize.xs,
  lineHeight: 1.4,
  color: darkRed,
  fontWeight: fontWeight.bold,
});

const CardFooter = styled.div({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: spacing.xs,
  paddingTop: spacing.xs,
  borderTop: { style: "solid", color: lightGrey, width: "1px" },
  marginTop: "auto",
});

const CardFooterMeta = styled.span({
  fontSize: fontSize.xs,
  color: darkGrey,
});

const CardLineTotal = styled.span({
  fontSize: fontSize.m,
  fontWeight: fontWeight.heavy,
  color: black,
});

/**
 * Total-rad längst ner i paket-listan — en framträdande sammanfattning som
 * ersätter tabellens tfoot.
 */
const PackageTotalsRow = styled.div({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.s,
  padding: { xy: spacing.m },
  backgroundColor: mediumGrey,
  borderRadius: "10px",
  marginTop: spacing.s,
});

const PackageTotalsLabel = styled.span({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: black,
});

const PackageTotalsValues = styled.div({
  display: "flex",
  flexDirection: "row",
  alignItems: "baseline",
  gap: spacing.m,
  flexWrap: "wrap",
});

const PackageTotalsCarbs = styled.span({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: darkGreen,
});

const PackageTotalsPrice = styled.span({
  fontSize: fontSize.l,
  fontWeight: fontWeight.heavy,
  color: black,
});

/**
 * Desktop-tabell för paket-rader. På mobil använder vi kortlayouten ovan
 * istället — växling sker via `isMobile`.
 */
const TableWrap = styled.div({
  marginBottom: spacing.m,
});

const Table = styled.table({
  width: "100%",
  borderCollapse: "collapse",
  fontSize: fontSize.s,
});

const Th = styled.th({
  textAlign: "left",
  padding: { xy: spacing.s },
  borderBottom: { style: "solid", color: grey, width: "2px" },
  fontWeight: fontWeight.bold,
});

const ThNum = styled.th({
  textAlign: "right",
  padding: { xy: spacing.s },
  borderBottom: { style: "solid", color: grey, width: "2px" },
  fontWeight: fontWeight.bold,
});

const ThInclude = styled.th({
  textAlign: "center",
  width: "48px",
  padding: { xy: spacing.s },
  borderBottom: { style: "solid", color: grey, width: "2px" },
  fontWeight: fontWeight.bold,
  fontSize: fontSize.xs,
});

const Td = styled.td({
  padding: { xy: spacing.s },
  borderBottom: { style: "solid", color: lightGrey, width: "1px" },
  verticalAlign: "middle",
});

const TdNum = styled.td({
  textAlign: "right",
  padding: { xy: spacing.s },
  borderBottom: { style: "solid", color: lightGrey, width: "1px" },
  verticalAlign: "middle",
});

const TdInclude = styled.td({
  textAlign: "center",
  width: "48px",
  padding: { xy: spacing.s },
  borderBottom: { style: "solid", color: lightGrey, width: "1px" },
  verticalAlign: "middle",
});

const LineIncludeCheck = styled.input({
  width: "20px",
  height: "20px",
  cursor: "pointer",
  accentColor: green,
});

const totalRowTopBorder = {
  style: "solid" as const,
  color: grey,
  width: "2px",
};

const TotalLabelCell = styled.td({
  textAlign: "right",
  padding: { xy: spacing.s },
  borderTop: totalRowTopBorder,
  fontWeight: fontWeight.bold,
});

const TotalTdNum = styled.td({
  textAlign: "right",
  padding: { xy: spacing.s },
  borderTop: totalRowTopBorder,
  verticalAlign: "middle",
  fontWeight: fontWeight.bold,
});

const TotalValueCell = styled.td({
  textAlign: "right",
  padding: { xy: spacing.s },
  borderTop: totalRowTopBorder,
  fontWeight: fontWeight.heavy,
  fontSize: fontSize.m,
});

const ProductCell = styled.div({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.s,
});

const MiniThumb = styled.img({
  width: "48px",
  height: "48px",
  objectFit: "contain",
  borderRadius: "4px",
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  backgroundColor: white,
  flexShrink: 0,
});

const ProductMeta = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
});

const FlavorSelect = styled.select({
  marginTop: spacing.xxs,
  fontSize: fontSize.xs,
  maxWidth: "100%",
  alignSelf: "flex-start",
  padding: { top: "6px", right: spacing.s, bottom: "6px", left: spacing.xs },
  borderRadius: "4px",
  border: { xy: { style: "solid", color: black, width: "1px" } },
  backgroundColor: white,
  color: black,
  cursor: "pointer",
  ":focus": {
    outline: { style: "none" },
    border: { xy: { style: "solid", color: green, width: "1px" } },
  },
});

const ProductNameLink = styled.span({
  color: black,
  fontWeight: fontWeight.bold,
  cursor: "pointer",
  ":hover": { textDecoration: "underline", color: darkGreen },
});

const CarbHint = styled.span({
  fontSize: fontSize.xs,
  color: darkGrey,
});

const CarbMismatchNote = styled.span({
  display: "block",
  marginTop: spacing.xxs,
  fontSize: fontSize.xs,
  lineHeight: 1.45,
  color: darkRed,
  fontWeight: fontWeight.bold,
});

const EmptyCatalog = styled.p({
  fontSize: fontSize.s,
  color: darkGrey,
});

const HintBanner = styled.p({
  padding: { xy: spacing.m },
  backgroundColor: lightGrey,
  borderRadius: "8px",
  fontSize: fontSize.s,
});

const ErrorMsg = styled.p({
  textAlign: "center",
  padding: { xy: spacing.xxxxl },
  color: red,
  fontSize: fontSize.l,
});
